// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Physics events, as the presentation layer sees them.
//
// The rule engine drains `World.events` every simulation step. A renderer runs once every
// sixteen or so of those, so by the time it looks the queue is always empty — which is why
// `SimEvent.value`, an impact speed the solver computes for exactly this purpose, has never
// reached anything that could use it.
//
// This is the other end of `EventQueue.tap`: a bounded ring the simulation writes into and a
// consumer reads from at whatever rate it happens to run. `FeedRing` owns the ring itself and
// the drop accounting; what is here is the columns and the visitor.
//
// **Parallel typed arrays rather than objects**, because the write happens inside the
// simulation's own hot path. A busy multiball emits perhaps fifty events a second against a
// million steps, so the allocation would be affordable — but it would be an allocation the
// core makes only when a renderer is attached, which is exactly the sort of difference that
// makes a profile taken in the browser not describe the server.

import type { EventTap } from '@core/sim/events'
import { FEED_CAPACITY, FeedRing } from './feedRing'

export { FEED_CAPACITY }

/** What a consumer is handed for each event, in the order the simulation emitted them. */
export type FeedVisitor = (
  type: number,
  device: number,
  ball: number,
  step: number,
  value: number,
) => void

export class PresentationEvents {
  /** Optional synchronous observer; the public record callback and all feed cursors stay stable. */
  afterRecord: EventTap | undefined = undefined
  private readonly ring: FeedRing
  private readonly types: Int32Array
  private readonly devices: Int32Array
  private readonly balls: Int32Array
  private readonly steps: Float64Array
  private readonly values: Float64Array

  constructor(capacity: number = FEED_CAPACITY) {
    this.ring = new FeedRing(capacity)
    const n = this.ring.capacity
    this.types = new Int32Array(n)
    this.devices = new Int32Array(n)
    this.balls = new Int32Array(n)
    this.steps = new Float64Array(n)
    this.values = new Float64Array(n)
  }

  /**
   * Install this on a world's event queue.
   *
   * A bound arrow so it can be assigned straight to `world.events.tap` without the caller
   * having to remember what `this` is — a method reference here would silently record into
   * nothing, and recording into nothing looks exactly like a machine with no events.
   */
  readonly record: EventTap = (type, device, ball, step, value) => {
    const slot = this.ring.claim()
    this.types[slot] = type
    this.devices[slot] = device
    this.balls[slot] = ball
    this.steps[slot] = step
    this.values[slot] = value
    this.afterRecord?.(type, device, ball, step, value)
  }

  /** The cursor of a consumer that has seen everything. Pass to `read` next time. */
  get cursor(): number {
    return this.ring.cursor
  }

  /** Events recorded since the feed was created, including any already overwritten. */
  get recorded(): number {
    return this.ring.recorded
  }

  /** Events overwritten before anybody read them. A diagnostic, never a correction. */
  get dropped(): number {
    return this.ring.dropped
  }

  /**
   * Visit everything recorded since `cursor` and return the new cursor.
   *
   * A consumer that has fallen more than the ring's capacity behind is fast-forwarded to the
   * oldest event still held, and the gap is counted rather than reported as an error: the
   * frame it belongs to has already been drawn.
   */
  read(cursor: number, visit: FeedVisitor): number {
    const end = this.ring.cursor
    for (let i = this.ring.begin(cursor); i < end; i++) {
      const slot = this.ring.slotOf(i)
      visit(this.types[slot]!, this.devices[slot]!, this.balls[slot]!, this.steps[slot]!, this.values[slot]!)
    }
    return end
  }

  /** Forget everything, as when the machine is rebuilt. */
  clear(): void {
    this.ring.clear()
  }
}
